import { onAuthStateChanged } from "firebase/auth";
import { ref, runTransaction } from "firebase/database";
import { auth, db } from "../firebaseConfig.js";

const SUMMIT_QUEUE_KEY = "trekkin_summit_conquest_ops_queue_v1";
const SUMMIT_QUEUE_EVENT = "trekkin:summit-conquest-queue-updated";
const SUMMIT_CONFIRMED_EVENT = "trekkin:summit-conquest-confirmed";

function isOnlineNow() {
  try {
    return typeof navigator === "undefined" ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
}

function localDayKey(timestamp) {
  const date = new Date(Number(timestamp));
  if (!Number.isFinite(date.getTime())) return "invalid";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

export function buildSummitConquestOpId(waypointId, conqueredAt) {
  const safeWaypointId = String(waypointId || "summit")
    .replace(/[.#$\[\]/]/g, "-")
    .slice(0, 80);
  return `summit-${safeWaypointId}-${localDayKey(conqueredAt)}`;
}

function normalizePayload(payload = {}) {
  const altitude = Number(payload?.altitude);
  return {
    name: String(payload?.name || "Cume").trim() || "Cume",
    altitude: Number.isFinite(altitude) ? altitude : null,
    imageUrl: payload?.imageUrl ? String(payload.imageUrl) : null,
    icon: payload?.icon ? String(payload.icon) : null,
  };
}

function normalizeQueue(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      opId: String(item?.opId || "").trim(),
      uid: String(item?.uid || "").trim(),
      waypointId: String(item?.waypointId || "").trim(),
      conqueredAt: Number(item?.conqueredAt || 0),
      payload: normalizePayload(item?.payload),
      createdAt: Number(item?.createdAt || 0),
      attempts: Math.max(0, Number(item?.attempts || 0)),
      lastError: item?.lastError ? String(item.lastError).slice(0, 500) : null,
    }))
    .filter((item) => item.opId && item.uid && item.waypointId && item.conqueredAt > 0);
}

function readQueue() {
  try {
    const raw = localStorage.getItem(SUMMIT_QUEUE_KEY);
    return raw ? normalizeQueue(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

function dispatch(name, detail) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // ignore environments without CustomEvent
  }
}

function writeQueue(queue) {
  const normalized = normalizeQueue(queue);
  if (typeof localStorage === "undefined") {
    throw new Error("Armazenamento local indisponível.");
  }
  if (normalized.length) {
    const serialized = JSON.stringify(normalized);
    localStorage.setItem(SUMMIT_QUEUE_KEY, serialized);
    if (localStorage.getItem(SUMMIT_QUEUE_KEY) !== serialized) {
      throw new Error("Não foi possível confirmar a gravação local da conquista.");
    }
  } else {
    localStorage.removeItem(SUMMIT_QUEUE_KEY);
  }
  dispatch(SUMMIT_QUEUE_EVENT, { queue: normalized });
  return normalized;
}

export function applySummitConquestOperation(current, operation) {
  const currentValue = current && typeof current === "object" ? current : {};
  const receipts = currentValue.appliedOps && typeof currentValue.appliedOps === "object"
    ? { ...currentValue.appliedOps }
    : {};
  if (receipts[operation.opId] != null) return currentValue;

  const conqueredAt = Number(operation.conqueredAt);
  const previousFirst = Number(currentValue.firstConqueredAt || 0);
  const previousLast = Number(currentValue.lastConqueredAt || 0);
  const payload = normalizePayload(operation.payload);
  receipts[operation.opId] = conqueredAt;

  return {
    ...currentValue,
    waypointId: operation.waypointId,
    name: payload.name || currentValue.name || "Cume",
    altitude: payload.altitude ?? currentValue.altitude ?? null,
    imageUrl: payload.imageUrl || currentValue.imageUrl || null,
    icon: payload.icon || currentValue.icon || null,
    count: Math.max(0, Number(currentValue.count || 0)) + 1,
    firstConqueredAt: previousFirst > 0 ? Math.min(previousFirst, conqueredAt) : conqueredAt,
    lastConqueredAt: previousLast > 0 ? Math.max(previousLast, conqueredAt) : conqueredAt,
    appliedOps: receipts,
  };
}

export function enqueueSummitConquest({ uid, waypointId, conqueredAt = Date.now(), ...payload }) {
  const safeUid = String(uid || "").trim();
  const safeWaypointId = String(waypointId || "").trim();
  if (!safeUid || !safeWaypointId) throw new Error("Conquista sem utilizador ou waypoint.");

  const queue = readQueue();
  const opId = buildSummitConquestOpId(safeWaypointId, conqueredAt);
  const existing = queue.find((item) => item.uid === safeUid && item.opId === opId);
  if (existing) return { operation: existing, queue, alreadyQueued: true };

  const operation = {
    opId,
    uid: safeUid,
    waypointId: safeWaypointId,
    conqueredAt: Number(conqueredAt),
    payload: normalizePayload(payload),
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  const next = writeQueue([...queue, operation]);
  return { operation, queue: next, alreadyQueued: false };
}

export function loadSummitConquestQueue() {
  return readQueue();
}

export async function flushSummitConquestQueue(uid = auth.currentUser?.uid) {
  const safeUid = String(uid || "").trim();
  if (!safeUid) return { ok: false, reason: "signed_out", applied: 0, pending: readQueue().length };
  if (!isOnlineNow()) return { ok: false, reason: "offline", applied: 0, pending: readQueue().length };

  let queue = readQueue();
  let applied = 0;
  for (const operation of queue.filter((item) => item.uid === safeUid)) {
    try {
      const result = await runTransaction(
        ref(db, `users/${safeUid}/summitConquests/${operation.waypointId}`),
        (current) => applySummitConquestOperation(current, operation),
        { applyLocally: false }
      );
      const confirmed = result.committed
        && Number(result.snapshot.child(`appliedOps/${operation.opId}`).val()) === operation.conqueredAt;
      if (!confirmed) throw new Error("O servidor não confirmou a conquista.");

      queue = readQueue().filter((item) => !(item.uid === safeUid && item.opId === operation.opId));
      writeQueue(queue);
      applied += 1;
      dispatch(SUMMIT_CONFIRMED_EVENT, { operation });
    } catch (error) {
      const latest = readQueue();
      const pending = latest.find((item) => item.uid === safeUid && item.opId === operation.opId);
      if (pending) {
        pending.attempts += 1;
        pending.lastError = String(error?.message || error || "Falha de sincronização").slice(0, 500);
        try { writeQueue(latest); } catch {}
      }
      return {
        ok: false,
        reason: isOnlineNow() ? "server_not_confirmed" : "offline_during_flush",
        applied,
        pending: readQueue().length,
        error: String(error?.message || error),
      };
    }
  }
  return { ok: true, applied, pending: readQueue().length };
}

let autoFlushInitialized = false;
let autoFlushRunning = false;

export function initSummitConquestOpsAutoFlush() {
  if (autoFlushInitialized || typeof window === "undefined") return;
  autoFlushInitialized = true;

  const run = async () => {
    if (autoFlushRunning || !isOnlineNow() || !auth.currentUser?.uid) return;
    autoFlushRunning = true;
    try {
      await flushSummitConquestQueue(auth.currentUser.uid);
    } finally {
      autoFlushRunning = false;
    }
  };

  window.addEventListener("online", run);
  window.addEventListener("focus", run);
  window.addEventListener("pageshow", run);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") run();
  });
  window.addEventListener(SUMMIT_QUEUE_EVENT, run);
  onAuthStateChanged(auth, (user) => {
    if (user?.uid) run();
  });
  window.setTimeout(run, 1200);
}

export { SUMMIT_CONFIRMED_EVENT, SUMMIT_QUEUE_EVENT, SUMMIT_QUEUE_KEY };
